import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import AttachmentReader from "@/components/AttachmentReader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, GraduationCap, BookOpen, Video, FileText, Link2, PlayCircle, CheckCircle2, MoreVertical, Pencil, Trash2, ExternalLink, BarChart3, Check, ShieldAlert, Send, Paperclip, Image as ImageIcon, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, permCan } from "@/lib/constants";

const TYPE_ICON = { video: Video, document: FileText, lien: Link2 };

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function ResourceCard({ r, onProgress, canManage, can, onEdit, onDelete, onRemind, onCover, onAttach, onRead }) {
  const Icon = TYPE_ICON[r.type] || FileText;
  const done = r.progress?.status === "termine";
  const inProgress = r.progress?.status === "en_cours";
  const embed = r.type === "video" ? ytEmbed(r.url) : null;
  const isMandatory = !!r.obligatoire && (r.cible || "formation") === "formation";
  const base = api.defaults.baseURL.replace(/\/api$/, "");
  const coverSrc = r.cover_url ? `${base}${r.cover_url}?v=${(r.cover_key || "").slice(-10)}` : null;
  const atts = r.attachments || [];
  return (
    <div className="rounded-xl border bg-white overflow-hidden flex flex-col" data-testid={`resource-${r.id}`}>
      {coverSrc ? (
        <div className="aspect-video bg-slate-100"><img src={coverSrc} alt={r.titre} className="w-full h-full object-cover" data-testid={`resource-cover-${r.id}`} /></div>
      ) : embed ? (
        <div className="aspect-video bg-black"><iframe src={embed} title={r.titre} className="w-full h-full" allowFullScreen /></div>
      ) : (
        <div className="aspect-video bg-slate-50 flex items-center justify-center"><Icon className="h-10 w-10 text-slate-300" /></div>
      )}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-sm">{r.titre}</p>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {isMandatory && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700" data-testid={`resource-mandatory-${r.id}`}><ShieldAlert className="h-3 w-3" />Obligatoire</span>}
              {inProgress && <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid={`resource-inprogress-${r.id}`}>En cours</span>}
              {r.categorie && <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{r.categorie}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {done && <CheckCircle2 className="h-5 w-5 text-emerald-500" data-testid={`resource-done-${r.id}`} />}
            {canManage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`resource-menu-${r.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {can("edit") && <DropdownMenuItem onClick={() => onEdit(r)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>}
                  {can("create") && <DropdownMenuItem onClick={() => onCover(r)} data-testid={`resource-cover-btn-${r.id}`}><ImageIcon className="h-4 w-4 mr-2" />Image de couverture</DropdownMenuItem>}
                  {can("create") && <DropdownMenuItem onClick={() => onAttach(r)} data-testid={`resource-attach-btn-${r.id}`}><Paperclip className="h-4 w-4 mr-2" />Ajouter une pièce jointe</DropdownMenuItem>}
                  {isMandatory && can("edit") && can("create") && <DropdownMenuItem onClick={() => onRemind(r)} data-testid={`resource-remind-${r.id}`}><Send className="h-4 w-4 mr-2" />Relancer les retardataires</DropdownMenuItem>}
                  {can("delete") && <DropdownMenuItem onClick={() => onDelete(r.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {r.description && <p className="text-xs text-slate-500 line-clamp-2">{r.description}</p>}
        {r.duree && <p className="text-[11px] text-slate-400">Durée : {r.duree}</p>}
        {atts.length > 0 && (
          <div className="pt-1">
            <Button variant="outline" size="sm" onClick={() => onRead(r)} className="border-brand-600 text-brand-700 hover:bg-brand-50" data-testid={`resource-read-${r.id}`}>
              <BookOpenCheck className="h-4 w-4 mr-1.5" />Lecture
              {atts.length > 1 && <span className="ml-1.5 rounded-full bg-brand-100 px-1.5 text-[11px] text-brand-700">{atts.length}</span>}
            </Button>
          </div>
        )}
        <div className="mt-auto flex items-center gap-2 pt-2">
          {(r.url && (r.type !== "video" || !embed)) && (
            <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-brand-700 hover:underline" data-testid={`resource-open-${r.id}`}><ExternalLink className="h-4 w-4" />Ouvrir</a>
          )}
          {done ? (
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => onProgress(r, "a_faire")} data-testid={`resource-undo-${r.id}`}>Marquer à revoir</Button>
          ) : (
            <Button size="sm" className="ml-auto bg-brand-600 hover:bg-brand-700 text-white" onClick={() => onProgress(r, "termine")} data-testid={`resource-complete-${r.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" />Marquer terminé</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressOverview({ data, onExport }) {
  if (!data) return <Loader />;
  if (data.total === 0) return <EmptyState icon={BarChart3} title="Aucune formation" subtitle="Ajoutez des ressources de formation pour suivre la progression des équipes." />;
  const fmap = data.formations;
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => onExport("csv")} data-testid="export-csv"><FileText className="h-4 w-4 mr-1.5" />Export Excel (CSV)</Button>
        <Button variant="outline" size="sm" onClick={() => onExport("pdf")} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1.5" />Export PDF</Button>
      </div>
      <div className="rounded-xl border bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="progress-overview-table">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="text-left font-semibold px-4 py-2.5 sticky left-0 bg-slate-50 min-w-[180px]">Collaborateur</th>
              <th className="text-center font-semibold px-3 py-2.5 whitespace-nowrap">Terminées</th>
              {fmap.map((f) => <th key={f.id} className="px-2 py-2.5 text-[11px] font-medium text-slate-500 max-w-[120px]"><div className="truncate" title={f.titre}>{f.titre}</div></th>)}
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const ds = new Set(u.done_ids);
              const pct = data.total ? Math.round((u.done_count / data.total) * 100) : 0;
              return (
                <tr key={u.user_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`overview-row-${u.user_id}`}>
                  <td className="px-4 py-2.5 sticky left-0 bg-white">
                    <p className="font-medium">{u.name}</p>
                    <p className="text-[11px] text-slate-400">{ROLE_LABELS[u.role] || u.role}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-brand-500" style={{ width: `${pct}%` }} /></div>
                      <span className="font-mono text-xs whitespace-nowrap" data-testid={`overview-count-${u.user_id}`}>{u.done_count}/{data.total}</span>
                    </div>
                  </td>
                  {fmap.map((f) => (
                    <td key={f.id} className="text-center px-2 py-2.5">
                      {ds.has(f.id) ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <span className="text-slate-200">—</span>}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

export default function Resources() {  const { role, permissions } = useAuth();
  const can = (a) => role === "super_admin" || permCan(permissions, "ressources", a);
  const canManage = can("create") || can("edit") || can("delete");
  const canFollow = can("edit");
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("formation");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [overview, setOverview] = useState(null);
  const [attPreview, setAttPreview] = useState(null);

  const load = () => api.get("/resources").then((r) => setItems(r.data));
  const loadStats = () => api.get("/resources-stats").then((r) => setStats(r.data)).catch(() => {});
  const loadOverview = () => api.get("/resources-progress-overview").then((r) => setOverview(r.data)).catch(() => {});
  useEffect(() => { load(); loadStats(); if (canFollow) loadOverview(); }, []);

  const schema = [
    { name: "titre", label: "Titre", required: true, col: 2 },
    { name: "cible", label: "Espace", type: "select", options: [{ value: "formation", label: "Formation" }, { value: "guide", label: "Guide utilisateur" }] },
    { name: "type", label: "Type", type: "select", options: [{ value: "video", label: "Vidéo" }, { value: "document", label: "Document" }, { value: "lien", label: "Lien" }] },
    { name: "categorie", label: "Catégorie / thème" },
    { name: "duree", label: "Durée (ex : 12 min)" },
    { name: "url", label: "URL (vidéo YouTube, document ou lien)", col: 2 },
    { name: "ordre", label: "Ordre d'affichage", type: "number" },
    { name: "obligatoire", label: "Formation obligatoire", type: "select", options: [{ value: "false", label: "Non" }, { value: "true", label: "Oui" }] },
    { name: "description", label: "Description", type: "textarea", col: 2 },
  ];

  const save = async (form) => {
    try {
      const payload = { ...form, ordre: Number(form.ordre) || 0, obligatoire: form.obligatoire === true || form.obligatoire === "true" };
      if (editing) await api.put(`/resources/${editing.id}`, payload); else await api.post("/resources", payload);
      toast.success(editing ? "Ressource mise à jour" : "Ressource créée"); load(); loadStats(); if (canFollow) loadOverview();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette ressource ?")) return; await api.delete(`/resources/${id}`); toast.success("Supprimée"); load(); loadStats(); if (canFollow) loadOverview(); };
  const setProgress = async (r, status) => {
    try { await api.post("/resource-progress", { resource_id: r.id, status }); load(); loadStats();
      if (status === "termine") toast.success("Marqué comme terminé"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openReader = (r) => {
    setAttPreview(r);
    if (!r.progress?.status || r.progress.status === "a_faire") {
      api.post("/resource-progress", { resource_id: r.id, status: "en_cours" }).then(() => { load(); loadStats(); }).catch(() => {});
    }
  };
  const remind = async (r) => {
    try { const { data } = await api.post(`/resources/${r.id}/remind`);
      toast.success(data.notified > 0 ? `${data.notified} collaborateur(s) relancé(s)` : "Tous les collaborateurs ont déjà terminé cette formation"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const exportOverview = async (fmt) => {
    try {
      const res = await api.get(`/resources-progress-overview/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `suivi-formations.${fmt === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const pickFile = (accept, cb) => {
    const i = document.createElement("input");
    i.type = "file"; if (accept) i.accept = accept;
    i.onchange = () => i.files[0] && cb(i.files[0]);
    i.click();
  };
  const uploadCover = (r) => pickFile("image/*", async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/resources/${r.id}/cover`, fd); toast.success("Image de couverture mise à jour"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  });
  const uploadAttachment = (r) => pickFile("", async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/resources/${r.id}/attachment`, fd); toast.success("Pièce jointe ajoutée"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  });
  const deleteAttachment = async (r, i) => {
    if (!window.confirm("Supprimer cette pièce jointe ?")) return;
    try { await api.delete(`/resources/${r.id}/attachment/${i}`); setAttPreview(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (items === null) return <Loader />;
  const byCible = (cible) => items.filter((r) => (r.cible || "formation") === cible);

  const grid = (list, empty) => list.length === 0 ? <EmptyState icon={BookOpen} title="Aucune ressource" subtitle={empty} /> : (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.map((r) => <ResourceCard key={r.id} r={r} onProgress={setProgress} canManage={canManage} can={can} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={remove} onRemind={remind} onCover={uploadCover} onAttach={uploadAttachment} onRead={openReader} />)}
    </div>
  );

  return (
    <div>
      <PageHeader title="Ressources" subtitle="Formation des collaborateurs & guides d'utilisation">
        {stats && <div className="rounded-lg border bg-white px-4 py-2 mr-2 hidden sm:block"><p className="text-[10px] uppercase font-mono text-slate-400">Formations terminées</p><p className="font-mono font-bold text-brand-700" data-testid="resources-progress">{stats.termine}/{stats.total_formation}</p></div>}
        {can("create") && <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-resource" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle ressource</Button>}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="formation" data-testid="tab-formation"><GraduationCap className="h-4 w-4 mr-1.5" />Formation</TabsTrigger>
          <TabsTrigger value="guide" data-testid="tab-guides"><BookOpen className="h-4 w-4 mr-1.5" />Guides utilisateur</TabsTrigger>
          {canFollow && <TabsTrigger value="suivi" data-testid="tab-suivi"><BarChart3 className="h-4 w-4 mr-1.5" />Suivi RH</TabsTrigger>}
        </TabsList>
        <TabsContent value="formation">{grid(byCible("formation"), "Ajoutez des vidéos et documents de formation.")}</TabsContent>
        <TabsContent value="guide">{grid(byCible("guide"), "Ajoutez des guides d'utilisation du système.")}</TabsContent>
        {canFollow && <TabsContent value="suivi"><ProgressOverview data={overview} onExport={exportOverview} /></TabsContent>}
      </Tabs>

      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier la ressource" : "Nouvelle ressource"} schema={schema} initial={editing} onSubmit={save} />
      <AttachmentReader resource={attPreview} onOpenChange={(o) => !o && setAttPreview(null)} canManage={can("delete")} onDelete={deleteAttachment} />
    </div>
  );
}
